import {
	Avatar,
	Box,
	ButtonBase,
	Grid2,
	InputBase,
	Skeleton,
	Tooltip,
	Typography,
} from '@mui/material';
import { RedFlag, SearchIcon } from '../../assets/icons/Icons';
import CustomSwitch from '../../components/CustomSwitch';
import FilterTag from '../../components/FilterTag';
import { MAIN_THEME_COLOR } from '../../assets/theme/Colors';
import { ChatList, Tag } from '../../types';
import { getLastChatDate } from '../../Utils/Helpers';

interface SelectedChat {
	phoneNumber: string;
	name: string;
	email: string;
	isConsultantNeeded: boolean;
}

interface SearchWithTagsSectionProps {
	title: string;
	placeholder: string;
	onSearch: (searchQuery: string) => void;
	tagList: Array<Tag>;
	selectedTag: string;
	onSelectTag: (tag: string) => void;
	searchedText: string;
	showConsultantFilter: boolean;
	consultantOnly: boolean;
	onConsultantOnlyChange: (enabled: boolean) => void;
}

interface RecentChatProps {
	chats: Array<ChatList>;
	selectedPhoneNumber: string;
	onClickRecentChat: (chat: SelectedChat) => void;
	onSearchChatList: (name: string) => void;
	onChangeGridVisibility: () => void;
	loading: boolean;
	tagList: Array<Tag>;
	onSelectTag: (value: string) => void;
	selectedTag: string;
	searchedText: string;
	onLoadMore: () => void;
	hasMore: boolean;
	loadingMore: boolean;
	showConsultantFilter: boolean;
	consultantOnly: boolean;
	onConsultantOnlyChange: (enabled: boolean) => void;
}

interface ChatNumbersListProps {
	chatListData: Array<ChatList>;
	onViewRecentChat: (chat: SelectedChat) => void;
	selectedPhoneNumber: string;
	onSearchChatList: (search: string) => void;
	onChangeGridVisibility: () => void;
	loading: boolean;
	tagList: Array<Tag>;
	onSelectTag: (value: string) => void;
	selectedTag: string;
	searchedText: string;
	onLoadMore: () => void;
	hasMore: boolean;
	loadingMore: boolean;
	showConsultantFilter: boolean;
	consultantOnly: boolean;
	onConsultantOnlyChange: (enabled: boolean) => void;
}

const SearchWithTagsSection: React.FC<SearchWithTagsSectionProps> = ({
	title,
	placeholder,
	onSearch,
	tagList,
	selectedTag,
	onSelectTag,
	searchedText,
	showConsultantFilter,
	consultantOnly,
	onConsultantOnlyChange,
}) => (
	<Box
		sx={{
			px: 2,
			pt: 2,
			pb: 1.5,
			borderBottom: '1px solid #E9EDEF',
			backgroundColor: '#FFFFFF',
		}}>
		<Typography
			sx={{
				fontSize: { xs: '18px', sm: '20px' },
				lineHeight: 1.25,
				fontWeight: 700,
				mb: 1.5,
			}}>
			{title}
		</Typography>
		<Grid2
			container
			alignItems="center"
			gap={1.25}
			sx={{
				minHeight: 38,
				px: 1.5,
				backgroundColor: '#F0F2F5',
				border: '1px solid transparent',
				borderRadius: '6px',
				'&:focus-within': {
					borderColor: '#A7DED2',
					backgroundColor: '#FFFFFF',
				},
			}}>
			<SearchIcon />
			<InputBase
				value={searchedText}
				type="search"
				sx={{
					flex: 1,
					minWidth: 0,
					fontSize: '13px',
					color: '#111B21',
				}}
				placeholder={
					placeholder === 'Search conversations'
						? 'Search chats'
						: placeholder
				}
				onChange={event => onSearch(event.target.value)}
			/>
		</Grid2>
		<Grid2
			container
			gap={0.75}
			mt={1.25}
			wrap="nowrap"
			sx={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
			{tagList.map(item => (
				<FilterTag
					key={item.name}
					name={item.name}
					count={item.count}
					isSelected={selectedTag === item.name}
					onSelect={onSelectTag}
				/>
			))}
		</Grid2>
		{showConsultantFilter && (
			<Grid2
				container
				alignItems="center"
				justifyContent="space-between"
				sx={{ mt: 1.25 }}>
				<Typography fontSize="12px" fontWeight={600} color="#54656F">
					<Box
						component="span"
						sx={{ display: { xs: 'inline', sm: 'none' } }}>
						Consultant only
					</Box>
					<Box
						component="span"
						sx={{ display: { xs: 'none', sm: 'inline' } }}>
						Consultant mode only
					</Box>
				</Typography>
				<CustomSwitch
					isChecked={consultantOnly}
					handleChange={event =>
						onConsultantOnlyChange(event.target.checked)
					}
				/>
			</Grid2>
		)}
	</Box>
);

function getAvatarLabel(name: string) {
	if (name && name !== 'Unknown') {
		return name.trim().charAt(0).toUpperCase();
	}

	return 'V';
}

function getListTimestamp(timestamp: string) {
	const date = getLastChatDate(timestamp.split('|')[0]);
	return date === 'Today' ? timestamp.split('|')[1]?.trim() : date;
}

function RecentChatWindow({
	chats,
	selectedPhoneNumber,
	onClickRecentChat,
	onSearchChatList,
	onChangeGridVisibility,
	loading,
	tagList,
	onSelectTag,
	selectedTag,
	searchedText,
	onLoadMore,
	hasMore,
	loadingMore,
	showConsultantFilter,
	consultantOnly,
	onConsultantOnlyChange,
}: RecentChatProps) {
	function handleScroll(event: React.UIEvent<HTMLDivElement>) {
		const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
		if (
			hasMore &&
			!loadingMore &&
			scrollHeight - scrollTop - clientHeight < 96
		) {
			onLoadMore();
		}
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<SearchWithTagsSection
				title="Conversations"
				placeholder="Search conversations"
				onSearch={onSearchChatList}
				tagList={tagList}
				selectedTag={selectedTag}
				onSelectTag={onSelectTag}
				searchedText={searchedText}
				showConsultantFilter={showConsultantFilter}
				consultantOnly={consultantOnly}
				onConsultantOnlyChange={onConsultantOnlyChange}
			/>

			<Box
				onScroll={handleScroll}
				sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
				{loading ? (
					<Box sx={{ px: 1.5, py: 1 }}>
						{[...Array(7)].map((_, index) => (
							<Grid2
								key={index}
								container
								alignItems="center"
								gap={1.5}
								sx={{ py: 1.25 }}>
								<Skeleton
									variant="circular"
									width={44}
									height={44}
								/>
								<Box sx={{ flex: 1 }}>
									<Skeleton width="58%" height={20} />
									<Skeleton width="84%" height={16} />
								</Box>
							</Grid2>
						))}
					</Box>
				) : chats.length > 0 ? (
					chats.map(value => {
						const isSelected =
							value.phoneNumber === selectedPhoneNumber;
						const displayName = value.name || 'Visitor';

						return (
							<ButtonBase
								key={value.phoneNumber}
								sx={{
									width: '100%',
									display: 'flex',
									alignItems: 'center',
									textAlign: 'left',
									px: 1.5,
									backgroundColor: isSelected
										? '#E7F8F2'
										: '#FFFFFF',
									'&:hover': { backgroundColor: '#F5F7F7' },
								}}
								onClick={() => {
									onClickRecentChat({
										phoneNumber: value.phoneNumber,
										name: value.name,
										email: value.email || '',
										isConsultantNeeded:
											value.isConsultantNeeded,
									});
									onChangeGridVisibility();
								}}>
								<Avatar
									sx={{
										width: 44,
										height: 44,
										fontSize: '14px',
										fontWeight: 700,
										backgroundColor: isSelected
											? '#00A884'
											: '#D9E4E1',
										color: isSelected
											? '#FFFFFF'
											: '#3B4A54',
									}}>
									{getAvatarLabel(value.name)}
								</Avatar>
								<Box
									sx={{
										flex: 1,
										minWidth: 0,
										ml: 1.5,
										py: 1.5,
										borderBottom: '1px solid #E9EDEF',
									}}>
									<Grid2
										container
										alignItems="center"
										justifyContent="space-between"
										wrap="nowrap"
										gap={1}>
										<Typography
											sx={{
												fontSize: '14px',
												fontWeight: 600,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}>
											{displayName}
										</Typography>
										<Typography
											sx={{
												fontSize: '10px',
												color:
													value.unreadMessageCount !==
													0
														? MAIN_THEME_COLOR
														: '#667781',
												whiteSpace: 'nowrap',
											}}>
											{getListTimestamp(value.timestamp)}
										</Typography>
									</Grid2>
									<Grid2
										container
										alignItems="center"
										justifyContent="space-between"
										wrap="nowrap"
										gap={1}
										mt={0.25}>
										<Typography
											sx={{
												fontSize: '12px',
												color: '#667781',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												flex: 1,
											}}>
											{value.lastMessage ||
												'No messages yet'}
										</Typography>
										<Grid2
											container
											alignItems="center"
											gap={0.75}>
											{value.isConsultantNeeded && (
												<Tooltip title="Consultant attention needed">
													<Box
														component="span"
														sx={{
															display: 'flex',
														}}>
														<RedFlag color="#F15C6D" />
													</Box>
												</Tooltip>
											)}
											{!isSelected &&
												value.unreadMessageCount !==
													0 && (
													<Box
														sx={{
															minWidth: 19,
															height: 19,
															px: 0.6,
															borderRadius:
																'10px',
															backgroundColor:
																MAIN_THEME_COLOR,
															color: '#FFFFFF',
															display: 'grid',
															placeItems:
																'center',
															fontSize: '10px',
															fontWeight: 700,
														}}>
														{
															value.unreadMessageCount
														}
													</Box>
												)}
										</Grid2>
									</Grid2>
								</Box>
							</ButtonBase>
						);
					})
				) : (
					<Box
						sx={{
							height: '100%',
							minHeight: 220,
							display: 'grid',
							placeItems: 'center',
							px: 3,
						}}>
						<Box sx={{ textAlign: 'center' }}>
							<Box
								sx={{
									width: 44,
									height: 44,
									borderRadius: '50%',
									backgroundColor: '#E7F8F2',
									color: MAIN_THEME_COLOR,
									display: 'grid',
									placeItems: 'center',
									mx: 'auto',
									mb: 1.25,
									fontSize: '20px',
								}}>
								...
							</Box>
							<Typography fontSize="13px" fontWeight={600}>
								No conversations found
							</Typography>
							<Typography
								fontSize="12px"
								color="#667781"
								mt={0.5}>
								Try a different search or filter.
							</Typography>
						</Box>
					</Box>
				)}
				{!loading && chats.length > 0 && loadingMore && (
					<Box
						sx={{ display: 'grid', placeItems: 'center', py: 1.5 }}>
						<Skeleton variant="circular" width={22} height={22} />
					</Box>
				)}
			</Box>
		</Box>
	);
}

function ChatNumbersList({
	chatListData,
	onViewRecentChat,
	selectedPhoneNumber,
	onSearchChatList,
	loading,
	onChangeGridVisibility,
	tagList,
	onSelectTag,
	selectedTag,
	searchedText,
	onLoadMore,
	hasMore,
	loadingMore,
	showConsultantFilter,
	consultantOnly,
	onConsultantOnlyChange,
}: ChatNumbersListProps) {
	return (
		<Grid2
			component="aside"
			size={{ xs: 12, sm: 4, xl: 3 }}
			sx={{
				height: '100%',
				minWidth: 0,
				backgroundColor: '#FFFFFF',
				borderRight: { md: '1px solid #DDE2E4' },
				overflow: 'hidden',
			}}>
			<RecentChatWindow
				chats={chatListData}
				onClickRecentChat={onViewRecentChat}
				selectedPhoneNumber={selectedPhoneNumber}
				onSearchChatList={onSearchChatList}
				onChangeGridVisibility={onChangeGridVisibility}
				loading={loading}
				onSelectTag={onSelectTag}
				selectedTag={selectedTag}
				tagList={tagList}
				searchedText={searchedText}
				onLoadMore={onLoadMore}
				hasMore={hasMore}
				loadingMore={loadingMore}
				showConsultantFilter={showConsultantFilter}
				consultantOnly={consultantOnly}
				onConsultantOnlyChange={onConsultantOnlyChange}
			/>
		</Grid2>
	);
}

export default ChatNumbersList;
